import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuthStore } from "@/modules/auth";

type WSStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

type Handler = (eventType: string, payload: unknown, id?: number) => void;

type Subscription = {
  channel: string;
  handler: Handler;
  lastEventId: number | null;
};

type WSContext = {
  status: WSStatus;
  subscribe: (channel: string, handler: Handler) => () => void;
  publishLocal: (channel: string, eventType: string, payload: unknown) => void;
};

const Ctx = createContext<WSContext | null>(null);

const BACKOFF = [500, 1000, 2000, 4000, 8000, 16000, 30000];

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [status, setStatus] = useState<WSStatus>("idle");
  const wsRef = useRef<WebSocket | null>(null);
  const subsRef = useRef<Map<string, Set<Subscription>>>(new Map());
  const lastIdRef = useRef<Map<string, number>>(new Map());
  const reconnectAttempt = useRef(0);
  const closedByCaller = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      closedByCaller.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      setStatus("idle");
      return;
    }
    closedByCaller.current = false;

    const connect = () => {
      const pageSecure = window.location.protocol === "https:";
      const proto = pageSecure ? "wss" : "ws";
      const raw = (import.meta.env.VITE_WS_BASE as string | undefined) ?? "/ws";
      const isAbsolute = /^wss?:\/\//i.test(raw) || /^https?:\/\//i.test(raw);
      let url: string;
      if (isAbsolute) {
        let swapped = raw.replace(/^https:\/\//i, "wss://").replace(/^http:\/\//i, "ws://");
        if (pageSecure) swapped = swapped.replace(/^ws:\/\//i, "wss://");
        url = swapped.endsWith("/") ? swapped : swapped + "/";
      } else {
        const path = raw.startsWith("/") ? raw : `/${raw}`;
        url = `${proto}://${window.location.host}${path}${path.endsWith("/") ? "" : "/"}`;
      }
      setStatus(reconnectAttempt.current === 0 ? "connecting" : "reconnecting");
      const ws = new WebSocket(url, [`bearer.${accessToken}`]);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempt.current = 0;
        setStatus("open");
        for (const channel of subsRef.current.keys()) {
          const since = lastIdRef.current.get(channel);
          ws.send(JSON.stringify({ op: "subscribe", channel, since }));
        }
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.op === "event" || msg.op === "replay") {
            const subs = subsRef.current.get(msg.channel);
            if (typeof msg.id === "number") lastIdRef.current.set(msg.channel, msg.id);
            subs?.forEach((s) => s.handler(msg.eventType, msg.payload, msg.id));
          } else if (msg.op === "resync_required") {
            lastIdRef.current.delete(msg.channel);
            const subs = subsRef.current.get(msg.channel);
            subs?.forEach((s) => s.handler("__resync__", null));
          } else if (msg.op === "subscribed" && typeof msg.lastEventId === "number") {
            lastIdRef.current.set(msg.channel, msg.lastEventId);
          }
        } catch {
          // ignore malformed
        }
      };

      ws.onclose = (ev) => {
        wsRef.current = null;
        if (closedByCaller.current) {
          setStatus("closed");
          return;
        }
        if (ev.code === 4401) {
          setStatus("closed");
          return;
        }
        setStatus("reconnecting");
        const delay = BACKOFF[Math.min(reconnectAttempt.current, BACKOFF.length - 1)];
        reconnectAttempt.current += 1;
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // close handler will run next
      };
    };

    connect();

    return () => {
      closedByCaller.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [accessToken]);

  const value = useMemo<WSContext>(() => ({
    status,
    subscribe: (channel, handler) => {
      let set = subsRef.current.get(channel);
      const sub: Subscription = { channel, handler, lastEventId: null };
      if (!set) {
        set = new Set();
        subsRef.current.set(channel, set);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const since = lastIdRef.current.get(channel);
          wsRef.current.send(JSON.stringify({ op: "subscribe", channel, since }));
        }
      }
      set.add(sub);
      return () => {
        set!.delete(sub);
        if (set!.size === 0) {
          subsRef.current.delete(channel);
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ op: "unsubscribe", channel }));
          }
        }
      };
    },
    publishLocal: (channel, eventType, payload) => {
      const subs = subsRef.current.get(channel);
      subs?.forEach((s) => s.handler(eventType, payload));
    },
  }), [status]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWebSocket(): WSContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWebSocket must be used within WebSocketProvider");
  return ctx;
}
